import { Edit, Delete } from "@mui/icons-material";
import { FoodsIcon, MRTGrid } from "@foodpilot/foods";
import { usePackagingListContext } from "../PackagingListContext";
import { IconButton } from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { useState } from "react";
import ActionsPopover from "src/components/ActionsPopover/ActionsPopover";
import { URLHandler } from "src/services/URLHandler";
import { packagingGridKey, PackagingList } from "src/api/client-api/packagings/useGetAllPackagings";
import { useTranslation } from "react-i18next";
import { CREATE, PACKAGING, useFoodpilotContext } from "src/context/FoodpilotContext";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { usePackagingDelete } from "src/api/client-api/packagings/useDeletePackaging";

const PackagingsGrid = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const deletePackaging = usePackagingDelete();
    const {
        mrt_columns,
        packagings = [],
        isLoading,
        delayedSearchPackaging,
        packagingPage,
        setPackagingPage,
        sorting,
        setSorting,
        totalItems,
    } = usePackagingListContext();
    const { selectedCompany } = useFoodpilotContext();
    const [selectedRowItemId, setSelectedRowItemId] = useState<number | null>(null);
    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);

    const selectedPackaging = packagings?.find((pack) => pack.id === selectedRowItemId);

    const menuItems: MenuItemType[] = [
        ...(selectedPackaging?.canView ?
            [
                {
                    icon: <FoodsIcon icon="scoresIndicators" size={3} />,
                    label: t("Scores & indicateurs clés"),
                    onClick: () => {
                        if (selectedCompany && selectedRowItemId)
                            navigate(URLHandler.packaging.scores(selectedCompany.id, selectedRowItemId));
                    },
                },
                {
                    icon: <FoodsIcon icon="questionnaires" size={3} />,
                    label: t("Questionnaire"),
                    onClick: () => {
                        if (selectedCompany && selectedRowItemId)
                            navigate(URLHandler.packaging.ssq(selectedCompany.id, selectedRowItemId));
                    },
                },
                {
                    icon: <FoodsIcon icon="informations" size={3} />,
                    label: t("Informations"),
                    onClick: () => {
                        if (selectedCompany && selectedRowItemId)
                            navigate(URLHandler.packaging.information(selectedCompany.id, selectedRowItemId));
                    },
                },
            ]
        :   []),
        ...(selectedPackaging?.canModify ?
            [
                {
                    icon: <Edit />,
                    label: t("Modifier"),
                    onClick: () => {
                        if (selectedCompany && selectedRowItemId)
                            navigate(URLHandler.packaging.edit(selectedCompany.id, selectedRowItemId, 0));
                    },
                },
            ]
        :   []),
        ...(selectedPackaging?.canDelete ?
            [
                {
                    icon: <Delete />,
                    label: t("Supprimer"),
                    onClick: () => {
                        if (selectedRowItemId)
                            deletePackaging.mutate(selectedRowItemId, {
                                onSuccess: () => {
                                    queryClient.invalidateQueries([packagingGridKey]);
                                    setRowMenuAnchor(null);
                                },
                            });
                    },
                },
            ]
        :   []),
    ];

    return (
        <>
            <MRTGrid
                columns={mrt_columns.mrt_columns}
                data={packagings}
                globalFilterFn="contains"
                enableRowActions={true}
                enableRowSelection={false}
                enablePagination={true}
                enableSelectAll={false}
                enableColumnPinning={true}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        size: 80,
                        grow: false,
                    },
                }}
                renderRowActions={(actionProps) => {
                    const { row } = actionProps;
                    const packagingId = row.original.id;

                    return (
                        <IconButton
                            onClick={(e) => {
                                setSelectedRowItemId(packagingId);
                                setRowMenuAnchor(e.currentTarget);
                            }}
                            sx={{
                                transform: "translateX(-4px)",
                            }}
                        >
                            <MoreHoriz />
                        </IconButton>
                    );
                }}
                state={{
                    isLoading: isLoading,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    globalFilter: delayedSearchPackaging,
                    pagination: packagingPage,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                    columnSizing: mrt_columns.columnSizing,
                    sorting,
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                // Server-side pagination
                rowCount={totalItems}
                manualPagination={true}
                onPaginationChange={setPackagingPage}
                localization={{
                    actions: "",
                }}
                manualSorting={true}
                onSortingChange={setSorting}
            />
            <ActionsPopover menuItems={menuItems} anchor={rowMenuAnchor} closeWindow={() => setRowMenuAnchor(null)} />
        </>
    );
};
export default PackagingsGrid;
